import { createInstance, Types } from '@amplitude/analytics-browser'
import { plugin as engagementPlugin } from '@amplitude/engagement-browser'

import type { AmplitudeConfig, AmplitudeErrorReporter } from './config'
import { watchEngagementScript } from './engagement-load-watch'
import { consoleReporter, createLogger, reportOnce } from './logger'

export type AmplitudeClient = Types.BrowserClient

export interface AmplitudeInstance {
  client: AmplitudeClient
  /** Settles once `init` finished; `false` when it failed. */
  initialized: Promise<boolean>
  /** `true` once Guides & Surveys booted; `false` when not configured or it never boots. */
  guidesReady: Promise<boolean>
}

const instances = new Map<string, AmplitudeInstance>()

/** A developer mistake: loud where a developer sees it, reported where only users do. */
const misuse = (report: AmplitudeErrorReporter, message: string, details?: Record<string, unknown>) => {
  if (process.env.NODE_ENV !== 'production') {
    throw new Error(message)
  }
  report(message, { level: 'error', ...details })
}

const toReporter = (config: AmplitudeConfig): AmplitudeErrorReporter => reportOnce(config.onError ?? consoleReporter)

const toBrowserOptions = (config: AmplitudeConfig, report: AmplitudeErrorReporter): Types.BrowserOptions => ({
  serverZone: config.serverZone,
  // The floor: remote config (Data > Settings > Autocapture) overrides only switches set there.
  autocapture: false,
  remoteConfig: { fetchRemoteConfig: true },
  // Also handed to the Guides & Surveys plugin by the SDK, so both report through `onError`.
  loggerProvider: createLogger(report),
  logLevel: Types.LogLevel.Warn,
})

const engagementBooted = (report: AmplitudeErrorReporter): boolean => {
  const analytics = (window as { engagement?: { _analytics?: { hasBooted?: boolean } } }).engagement?._analytics
  // The loader stub answers every property with a queueing function; the real bundle has an object here.
  if (typeof analytics !== 'object') return false
  if (analytics.hasBooted === true) return true
  report('Guides & Surveys bundle loaded but did not boot', { level: 'warn' })
  return false
}

type EngagementPlugin = ReturnType<typeof engagementPlugin>

// Created before init so `window.engagement` queues calls made before the bundle arrives.
const createEngagementPlugin = (config: AmplitudeConfig): EngagementPlugin | undefined =>
  config.engagement &&
  engagementPlugin({
    ...(config.serverZone && { serverZone: config.serverZone }),
    locale: config.engagement.locale,
    autoRefreshIntervalSeconds: config.engagement.autoRefreshIntervalSeconds,
  })

// Added once the client is ready: only then does the add promise wait for the plugin to boot.
const addEngagement = (
  client: AmplitudeClient,
  plugin: EngagementPlugin | undefined,
  initialized: Promise<boolean>,
  report: AmplitudeErrorReporter,
): Promise<boolean> => {
  if (!plugin) return Promise.resolve(false)

  return initialized
    .then(async (isReady) => {
      if (!isReady) return false
      watchEngagementScript({ report })
      await client.add(plugin).promise
      return engagementBooted(report)
    })
    .catch((error: unknown) => {
      report('Guides & Surveys plugin failed to start', { level: 'error', error })
      return false
    })
}

const addSessionReplay = (
  client: AmplitudeClient,
  config: AmplitudeConfig,
  initialized: Promise<boolean>,
  report: AmplitudeErrorReporter,
) => {
  const { sessionReplay, loadSessionReplayPlugin: load } = config
  if (!sessionReplay) return
  if (!load) {
    misuse(report, 'Session replay is configured but `loadSessionReplayPlugin` is not set for this client')
    return
  }

  void Promise.all([initialized, load()])
    .then(async ([isReady, { sessionReplayPlugin }]) => {
      if (isReady) await client.add(sessionReplayPlugin(sessionReplay)).promise
    })
    .catch((error: unknown) => {
      report('Session replay plugin failed to load', { level: 'error', error })
    })
}

const createAmplitudeInstance = (config: AmplitudeConfig): AmplitudeInstance => {
  const client = createInstance()
  const report = toReporter(config)
  const engagement = createEngagementPlugin(config)

  const initialized = client
    .init(config.apiKey, config.userId, toBrowserOptions(config, report))
    .promise.then(() => true)
    .catch((error: unknown) => {
      report('Amplitude client failed to initialise', { level: 'error', error })
      return false
    })

  const guidesReady = addEngagement(client, engagement, initialized, report)
  addSessionReplay(client, config, initialized, report)

  window.addEventListener('pagehide', () => {
    client.setTransport('beacon')
    void client.flush()
  })

  return { client, initialized, guidesReady }
}

/** One client per api key (two would share the `AMP_<key>` cookie but not queues or plugins); the first config wins. */
export const getAmplitudeClient = (config: AmplitudeConfig): AmplitudeInstance => {
  if (!config.apiKey) {
    throw new Error('No amplitude project api key was set')
  }

  const existing = instances.get(config.apiKey)
  if (existing) return existing

  const instance = createAmplitudeInstance(config)
  instances.set(config.apiKey, instance)
  return instance
}

/** Test helper. */
export const resetAmplitudeClients = () => {
  instances.clear()
}
